import { useEffect, useState } from 'react'
import { useChamada } from './chamada.tsx'
import { EMOJIS, Foto, QuadroDaPessoa, Video } from './ReuniaoDaSala.tsx'
import './reuniao.css'
import './reuniao-celular.css'

/**
 * A reunião de uma sala do Escritório no celular (SPEC-20261011-0330), no jeito do
 * Meet de celular: quem apresenta (ou quem você fixou, ou a primeira pessoa) fica
 * grande, os outros numa tira que desliza, e os botões numa barra embaixo, no
 * alcance do polegar. Chat e pessoas sobem como folhas. Minimizar volta ao
 * Escritório e a chamada segue na bolha. Apresentar a tela fica no computador:
 * o navegador do celular não deixa.
 */
export function ReuniaoCelular({ cenario, onVoltar, onSair }: { cenario?: string; onVoltar: () => void; onSair: () => void }) {
  const ch = useChamada()
  const [fixado, setFixado] = useState<string | null>(null)
  const [folha, setFolha] = useState<'chat' | 'pessoas' | null>(null)
  const [emojis, setEmojis] = useState(false)
  const [texto, setTexto] = useState('')

  useEffect(() => {
    ch?.abrirChat(folha === 'chat')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folha])
  useEffect(() => () => ch?.abrirChat(false), [])

  if (!ch?.sala) return null
  const { quadros, quemApresenta, sala } = ch
  const outros = quadros.filter((q) => !q.eu)
  const grande = quemApresenta ? null : (quadros.find((q) => q.id === fixado) ?? outros[0] ?? quadros[0] ?? null)
  const tira = quemApresenta ? quadros : quadros.filter((q) => q.id !== grande?.id)
  const enviar = () => {
    ch.enviarMensagem(texto)
    setTexto('')
  }

  return (
    <div className="rcel" style={cenario ? { ['--rcel-fundo' as string]: `url(${cenario})` } : undefined}>
      <div className="rcel-fundo" aria-hidden />
      <header className="rcel-topo">
        <button type="button" className="rcel-redondo" aria-label="Minimizar: a chamada continua e você volta ao Escritório" onClick={onVoltar}>
          <i className="fas fa-chevron-down" aria-hidden />
        </button>
        <span className="rcel-titulo">
          <b>{sala.nome}</b>
          <small>{quadros.length === 1 ? 'Só você na chamada' : `${quadros.length} pessoas na chamada`}</small>
        </span>
        <button type="button" className="rcel-redondo" aria-label="Pessoas na chamada" onClick={() => setFolha('pessoas')}>
          <i className="fas fa-user-friends" aria-hidden />
        </button>
      </header>

      <main className="rcel-palco">
        <div className="rcel-grande">
          {quemApresenta ? (
            <div className="rs-quadro rcel-tela">
              <Video stream={quemApresenta.tela} className="rs-video rs-video--tela" />
              <span className="rs-nome">
                <i className="fas fa-desktop" aria-hidden /> {quemApresenta.nome === 'Você' ? 'Você está apresentando' : `${quemApresenta.nome} está apresentando`}
              </span>
            </div>
          ) : grande ? (
            <QuadroDaPessoa q={grande} fixado={grande.id === fixado} onFixar={fixado ? () => setFixado(null) : undefined} />
          ) : null}
          {quadros.length === 1 ? <p className="rcel-sozinho">Só você por aqui. Quem tocar nesta sala entra com você.</p> : null}
          <div className="rs-reacoes" aria-hidden>
            {ch.reacoes.map((r) => (
              <span key={r.id} className="rs-reacao" style={{ left: `${r.x}%` }}>
                <b>{r.emoji}</b>
                <small>{r.nome}</small>
              </span>
            ))}
          </div>
          {ch.balao && folha !== 'chat' ? (
            <button type="button" className="rcel-balao" onClick={() => setFolha('chat')}>
              <b>{ch.balao.nome}</b>
              <span>{ch.balao.texto}</span>
            </button>
          ) : null}
        </div>
        {tira.length ? (
          <div className="rcel-tira" role="list" aria-label="Outras pessoas: toque para ver grande">
            {tira.map((q) => (
              <button key={q.id} type="button" role="listitem" className="rcel-tira-item" aria-label={`Ver ${q.eu ? 'você' : q.nome} grande`} onClick={() => !quemApresenta && setFixado(q.id)}>
                <QuadroDaPessoa q={q} pequeno fixado={false} />
              </button>
            ))}
          </div>
        ) : null}
      </main>

      {emojis ? (
        <div className="rcel-emojis" role="menu" aria-label="Reações">
          {EMOJIS.slice(0, 6).map((e) => (
            <button
              key={e}
              type="button"
              role="menuitem"
              onClick={() => {
                ch.reagir(e)
                setEmojis(false)
              }}
            >
              {e}
            </button>
          ))}
        </div>
      ) : null}

      <footer className="rcel-barra">
        <button type="button" className={`rcel-ctl${ch.micLigado ? '' : ' rcel-ctl--off'}`} aria-label={ch.micLigado ? 'Desligar microfone' : 'Ligar microfone'} onClick={ch.alternarMic}>
          <i className={`fas ${ch.micLigado ? 'fa-microphone' : 'fa-microphone-slash'}`} aria-hidden />
        </button>
        <button type="button" className={`rcel-ctl${ch.camLigada ? '' : ' rcel-ctl--off'}`} aria-label={ch.camLigada ? 'Desligar câmera' : 'Ligar câmera'} onClick={ch.alternarCamera}>
          <i className={`fas ${ch.camLigada ? 'fa-video' : 'fa-video-slash'}`} aria-hidden />
        </button>
        <button type="button" className={`rcel-ctl${emojis ? ' rcel-ctl--on' : ''}`} aria-label="Reações" aria-expanded={emojis} onClick={() => setEmojis(!emojis)}>
          <i className="fas fa-smile" aria-hidden />
        </button>
        <button type="button" className={`rcel-ctl${ch.mao ? ' rcel-ctl--mao' : ''}`} aria-label={ch.mao ? 'Abaixar a mão' : 'Levantar a mão'} onClick={ch.alternarMao}>
          <i className="fas fa-hand-paper" aria-hidden />
        </button>
        <button type="button" className="rcel-ctl" aria-label="Mensagens na chamada" onClick={() => setFolha('chat')}>
          <i className="fas fa-comment-alt" aria-hidden />
          {ch.naoLidas ? <em className="rcel-conta">{ch.naoLidas}</em> : null}
        </button>
        <button type="button" className="rcel-ctl rcel-sair" aria-label="Sair da chamada" onClick={onSair}>
          <i className="fas fa-phone-slash" aria-hidden /> Sair
        </button>
      </footer>

      <div className={`rcel-veu${folha ? ' rcel-veu--on' : ''}`} onClick={() => setFolha(null)} aria-hidden />
      <section className={`rcel-folha${folha ? ' rcel-folha--on' : ''}`} aria-label={folha === 'chat' ? 'Mensagens na chamada' : 'Pessoas'} aria-hidden={!folha}>
        <span className="rcel-puxador" aria-hidden />
        <header>
          <b>{folha === 'chat' ? 'Mensagens na chamada' : `Pessoas · ${quadros.length}`}</b>
          <button type="button" className="rcel-fechar" aria-label="Fechar" onClick={() => setFolha(null)}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </header>
        {folha === 'pessoas' ? (
          <ul className="rcel-pessoas">
            {quadros.map((q) => (
              <li key={q.id}>
                <Foto nome={q.nome} foto={q.foto} tamanho={36} />
                <span>
                  {q.nome}
                  {q.eu ? <small> (você)</small> : null}
                </span>
                {q.mao ? <em aria-label="Mão levantada">✋</em> : null}
                <i className={`fas ${q.mic ? 'fa-microphone' : 'fa-microphone-slash rcel-mudo'}`} aria-label={q.mic ? 'Microfone ligado' : 'Microfone desligado'} />
              </li>
            ))}
          </ul>
        ) : folha === 'chat' ? (
          <>
            <div className="rcel-msgs">
              {ch.mensagens.length ? (
                ch.mensagens.map((m) => (
                  <div key={m.id} className={`rcel-msg${m.minha ? ' rcel-msg--minha' : ''}`}>
                    <small>{m.minha ? 'Você' : m.nome}</small>
                    <p>{m.texto}</p>
                  </div>
                ))
              ) : (
                <p className="rcel-vazio">Nenhuma mensagem ainda. Elas só aparecem para quem está na chamada.</p>
              )}
            </div>
            <form
              className="rcel-escrever"
              onSubmit={(e) => {
                e.preventDefault()
                enviar()
              }}
            >
              <input value={texto} maxLength={2000} placeholder="Mensagem para a sala" aria-label="Mensagem para todos na chamada" onChange={(e) => setTexto(e.target.value)} />
              <button type="submit" aria-label="Enviar" disabled={!texto.trim()}>
                <i className="fas fa-paper-plane" aria-hidden />
              </button>
            </form>
          </>
        ) : null}
      </section>
      {ch.aviso ? (
        <p className="rcel-aviso" role="status">
          {ch.aviso}
        </p>
      ) : null}
    </div>
  )
}
